import type { IModelConnection } from "@itwin/core-frontend";
import { QueryBinder, QueryRowFormat } from "@itwin/core-common";

import { Origin } from "../../domain/common/Origin";

/**
 * ECSQL to get the Origin (3D point) for a GeometricElement3d by ECInstanceId.
 * Only 3D elements have an Origin property; for 2D elements this will return no rows.
 */
const ECSQL_ORIGIN_FOR_ELEMENT = `
  SELECT
    g.Origin AS origin
  FROM bis.GeometricElement3d g
  WHERE g.ECInstanceId = ?
`;

/**
 * Returns the element's origin in model coordinates (meters/ft of the iModel).
 * Only works for 3D elements (bis.GeometricElement3d). For non-3D elements, returns undefined.
 */
export async function queryOriginForElement(
  iModel: IModelConnection,
  elementId: string,
): Promise<Origin | undefined> {

  const reader = iModel.createQueryReader(
    ECSQL_ORIGIN_FOR_ELEMENT,
    QueryBinder.from([elementId]),
    { rowFormat: QueryRowFormat.UseJsPropertyNames }
  );

  for await (const row of reader) {
    // With UseJsPropertyNames and `AS origin`, the property key is `origin`
    const r = row as any;
    const o = r.origin as { x?: number; y?: number; z?: number; X?: number; Y?: number; Z?: number } | undefined;
    if (!o)
      return undefined;

    // Build a real Origin instance (with toObject, etc.)
    const origin = Origin.fromObject({
      x: typeof o.x === "number" ? o.x : (o.X as number ?? 0),
      y: typeof o.y === "number" ? o.y : (o.Y as number ?? 0),
      z: typeof o.z === "number" ? o.z : (o.Z as number ?? 0),
    });

    return origin;
  }

  // No row → not a 3D element or no origin stored
  return undefined;
}


